import React, { useState, useEffect } from 'react';
import {
    Card,
    Grid,
    Chip,
    Dialog,
    DialogTitle,
    Pagination,
    PaginationItem,
    Stack,
    Switch
} from '@mui/material';
import Cookies from 'js-cookie';
import { DataGrid, useGridApiContext, useGridState } from '@mui/x-data-grid';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Typography, Button, Zoom, IconButton, TextField } from '@material-ui/core';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Person,
    Create,
    Clear,
    Start,
    RotateRight,
    DoneAll,
    ErrorOutline
} from '@mui/icons-material';
import { yellow } from '@mui/material/colors';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { styled } from '@material-ui/styles';
import { format } from 'date-fns';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import './styles.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import {
    getAllInstancesList,
    getAllInstances,
    preUpgrade,
    startUpgrade
} from '../../redux/Actions/instances.action';
import { saveClient, removeClient } from '../../redux/Actions/createInstance.action';
import Notifications from '../notifications/Notifications';
import { debounceRate } from '../../constants/constants.json';
import useStyles from './styles';
import Services from './form-components/Services';
import G2Review from './form-components/G2Review';

const escapeRegExp = (value) => value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: '#FFFFFF',
        color: '#000000',
        boxShadow: theme.shadows[1],
        fontSize: '14px',
        padding: '10px 20px'
    }
}));

const QuickSearchToolbar = (props) => {
    const classes = useStyles(props.theme);
    const [isSearchOpen, setSearchOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const { setPage, setSearchString } = props;
    useEffect(() => {
        const timeoutId = setTimeout(() => setSearchString(escapeRegExp(searchText)), debounceRate);
        setPage(0);
        return () => clearTimeout(timeoutId);
    }, [searchText, setSearchString, setPage]);

    return (
        <div className={classes.root}>
            {!isSearchOpen ? (
                <strong>
                    {'Name '}
                    <IconButton
                        aria-haspopup="true"
                        color="inherit"
                        onClick={() => {
                            setSearchOpen(true);
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faSearch}
                            className={classes.tableSearchIcon}
                            classes={{ root: classes.headerIcon }}
                        />
                    </IconButton>
                </strong>
            ) : (
                <TextField
                    variant="standard"
                    value={searchText}
                    onChange={(e) => {
                        setSearchText(e.target.value);
                    }}
                    placeholder="Search"
                    autoFocus
                    className={classes.searchTextField}
                    InputProps={{
                        classes: {
                            input: classes.resize
                        },
                        startAdornment: <SearchIcon fontSize="small" />,
                        endAdornment: (
                            <IconButton
                                title="Clear"
                                aria-label="Clear"
                                size="small"
                                onClick={() => {
                                    setSearchText('');
                                    setSearchOpen(false);
                                }}
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )
                    }}
                />
            )}
        </div>
    );
};

export default function DataTable(theme) {
    const classes = useStyles(theme);
    const dispatch = useDispatch();
    const [selectedValue, setSelectedValue] = useState('');
    const [rows, setRows] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const [searchString, setSearchString] = useState('');
    const [instanceData, setInstanceData] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [page, setPage] = React.useState(0);
    const [loadingPagination, setLoading] = useState(false);
    const [openBuildDialog, setOpenBuildDialog] = useState(false);
    const [currentClient, setCurrentClient] = useState({});
    const [openG2Review, setG2Review] = React.useState(false);
    const [activeParams, setActiveParams] = React.useState(false);

    async function fetchList() {
        setLoading(true);
        const data = {
            searchString,
            pageNo: page || 0,
            resultsPerPage: 10
        };
        const list = await getAllInstancesList(data);
        console.log('List --', list);
        setLoading(false);
        if (list[0] && list[0].total[0] && list[0].total[0].count) {
            setRowCount(list[0].total[0].count);
        } else {
            setRowCount(0);
        }
        if (list[0] && list[0].clients) {
            setInstanceData(list[0].clients);
        } else {
            setInstanceData([]);
        }
    }

    useEffect(() => {
        const instances = [...instanceData];
        if (instances && instances.length > 0) {
            instances.map((instance, i) => {
                instance.id = page * 10 + (i + 1);
                return instance;
            });
            setRows([...instances]);
        } else {
            setRows([]);
        }
    }, [instanceData]);

    useEffect(() => {
        fetchList();
    }, [dispatch, page, searchString]);

    const handleClickOpen = (selectedId) => {
        setSelectedValue(selectedId);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const renderButtons = (params) => (
        <div className={classes.actionButtonContainer}>
            <Button
                component={Link}
                to={`/instances/create/${params.row.tenantId}`}
                variant="text"
                className={classes.editButton}
                startIcon={<Create className={classes.editIcon} />}
            >
                Edit
            </Button>
            <Button
                onClick={() => {
                    handleClickOpen(params.row.tenantId);
                }}
                variant="text"
                startIcon={<Clear className={classes.editIcon} />}
            >
                Remove
            </Button>
        </div>
    );

    const renderName = (params) => {
        const matchDate = new Date(params.row.createdAt);
        matchDate.setMonth(matchDate.getMonth() + 2);
        let newInstance = false;
        if (matchDate > new Date()) {
            newInstance = true;
        }
        const tipText = `Added on : ${format(
            new Date(params.row.createdAt),
            'M-dd-yyyy'
        )}\nAdded by : ${params.row.createdBy}`;
        return (
            <Grid
                container
                sx={{
                    flexFlow: 'nowrap'
                }}
            >
                <LightTooltip
                    title={<span style={{ whiteSpace: 'pre-line' }}>{tipText}</span>}
                    TransitionComponent={Zoom}
                    className={classes.toolTip}
                >
                    <Person className={classes.nameIcon} />
                </LightTooltip>
                <Tooltip title={params.formattedValue} placement="top-start">
                    <Typography className={classes.materialIconText}>
                        {params.formattedValue}
                    </Typography>
                </Tooltip>
                {newInstance === true && (
                    <Chip label="New" size="small" className={classes.newChip} />
                )}
            </Grid>
        );
    };

    const renderUpdatedBy = (params) => {
        const { updatedBy } = params.row;
        return <Typography className={classes.updatedByText}>{updatedBy}</Typography>;
    };

    const setClientBuildSettings = (client) => {
        setCurrentClient(client);
        setOpenBuildDialog(true);
    };

    const startPreDeployment = async (services) => {
        setLoading(true);
        const response = await preUpgrade({
            services,
            tenantId: currentClient.tenantId,
            isRelease: !currentClient.isRelease,
            newReleaseVersion: currentClient.newReleaseVersion
        });
        if (response.message) toast.warning(response.message);
        else toast.success('Pre Deployment Started');
        console.log('client services: ', currentClient.services, 'response: ', response);
        setOpenBuildDialog(false);
        dispatch(getAllInstances({}));
        fetchList();
    };

    const startDeployment = async (client) => {
        const response = await startUpgrade({ tenantId: client.tenantId });
        console.log('deployment started: response: ', response);
        toast.success('Deployment Started');
        dispatch(getAllInstances({}));
        fetchList();
    };

    const renderUpgradeTo = (params) => (
        <div className={classes.buildButtonDiv}>
            <div style={{ float: 'left', width: '50%' }}>
                {params.id === 1 ? <div>Pre-Deployment</div> : ''}
                <span hidden={params.row.upgradeStatus?.preDeploymentStatus}>
                    <Button
                        variant="text"
                        className={classes.btnStart}
                        onClick={() => setClientBuildSettings(params.row)}
                    >
                        <Start fontSize="small" />
                        Start
                    </Button>
                </span>
                <span
                    hidden={
                        !params.row.upgradeStatus ||
                        (params.row.upgradeStatus &&
                            params.row.upgradeStatus.preDeploymentStatus !== 1)
                    }
                >
                    <span style={{ color: yellow[700] }}>
                        <RotateRight fontSize="small" sx={{ color: yellow[700] }} />
                        In-Progress
                    </span>
                </span>
                <span
                    hidden={
                        !params.row.upgradeStatus ||
                        (params.row.upgradeStatus &&
                            params.row.upgradeStatus.preDeploymentStatus !== 2)
                    }
                >
                    <Button
                        variant="text"
                        style={{ color: 'green' }}
                        onClick={() => setClientBuildSettings(params.row)}
                    >
                        <DoneAll fontSize="small" />
                        Completed
                    </Button>
                </span>
                <span
                    hidden={
                        !params.row.upgradeStatus ||
                        (params.row.upgradeStatus &&
                            params.row.upgradeStatus.preDeploymentStatus !== -1)
                    }
                >
                    <Button
                        variant="text"
                        style={{ color: 'red' }}
                        onClick={() => setClientBuildSettings(params.row)}
                    >
                        <ErrorOutline fontSize="small" />
                        Failed
                    </Button>
                </span>
            </div>
            <div style={{ float: 'right', width: '50%' }}>
                {params.id === 1 ? <div>Deployment</div> : ''}
                <span
                    hidden={params.row.upgradeStatus && params.row.upgradeStatus.deploymentStatus}
                >
                    <Button
                        variant="text"
                        className={classes.btnStart}
                        onClick={() => startDeployment(params.row)}
                        disabled={
                            !params.row.upgradeStatus ||
                            params.row.upgradeStatus.preDeploymentStatus !== 2
                        }
                    >
                        <Start fontSize="small" />
                        Start
                    </Button>
                </span>
                <span
                    hidden={
                        !params.row.upgradeStatus ||
                        (params.row.upgradeStatus &&
                            params.row.upgradeStatus.deploymentStatus !== 1)
                    }
                >
                    <span style={{ color: yellow[700] }}>
                        <RotateRight fontSize="small" sx={{ color: yellow[700] }} />
                        In-Progress
                    </span>
                </span>
                <span
                    hidden={
                        !params.row.upgradeStatus ||
                        (params.row.upgradeStatus &&
                            params.row.upgradeStatus.deploymentStatus !== 2)
                    }
                >
                    <Button
                        variant="text"
                        style={{ color: 'green' }}
                        onClick={() => startDeployment(params.row)}
                    >
                        <DoneAll fontSize="small" />
                        Completed
                    </Button>
                </span>
                <span
                    hidden={
                        !params.row.upgradeStatus ||
                        (params.row.upgradeStatus &&
                            params.row.upgradeStatus.deploymentStatus !== -1)
                    }
                >
                    <Button
                        variant="text"
                        style={{ color: 'red' }}
                        onClick={() => startDeployment(params.row)}
                    >
                        <ErrorOutline fontSize="small" />
                        Failed
                    </Button>
                </span>
            </div>
        </div>
    );

    const SimpleDialog = (props) => {
        const { onClose, selectedValue, open } = props;
        const [loading, setLoadingBtn] = React.useState(false);

        const handleClose = (event, reason) => {
            if (reason !== 'backdropClick') {
                onClose(selectedValue);
            }
        };

        const handleDelete = async () => {
            setLoadingBtn(true);
            try {
                await removeClient(selectedValue);
                if (Cookies.get('tenantId') === selectedValue) {
                    Cookies.remove('tenantId');
                }
                setLoadingBtn(false);
                setOpen(false);
                toast.success('Instance removed successfully');
                dispatch(getAllInstances({}));
                fetchList();
                if (rows.length === 1) {
                    setPage(page - 1);
                }
            } catch (error) {
                setLoadingBtn(false);
                setOpen(false);
                toast.error('Error in removing instance');
            }
        };

        return (
            <Dialog onClose={handleClose} open={open} disableEscapeKeyDown>
                <DialogTitle className={classes.popupTitle}>
                    Are you sure to delete
                    <br />
                    the instance?
                </DialogTitle>
                <Grid container className={classes.pb30}>
                    <span className={classes.popupButtonGrid}>
                        <Button variant="outlined" onClick={handleClose} className={classes.mx10}>
                            Cancel
                        </Button>
                        <LoadingButton
                            onClick={handleDelete}
                            loading={loading}
                            variant="outlined"
                            className={classes.mx10}
                        >
                            Remove
                        </LoadingButton>
                    </span>
                </Grid>
            </Dialog>
        );
    };

    const openG2ReviewDialog = (params) => {
        setActiveParams({
            tenantId: params.row.tenantId,
            g2Review: params.row.g2Review,
            name: params.row.name
        });
        setG2Review(true);
    };
    const closeG2Review = async () => {
        setG2Review(false);
        await fetchList();
    };
    const toggleG2Review = async () => {
        activeParams.g2Review = !activeParams.g2Review;
        await saveClient(activeParams);
        setG2Review(false);
        await fetchList();
    };
    const columns = [
        {
            field: 'id',
            headerName: '#',
            minWidth: 30,
            flex: 0.8,
            type: 'number',
            filter: false,
            sortable: false,
            renderHeader: () => (
                <Grid container className={classes.alignCenter}>
                    #
                </Grid>
            ),
            renderCell: (params) => (
                <Grid container className={classes.alignCenter}>
                    {params.row.id}
                </Grid>
            )
        },
        {
            field: 'name',
            flex: 4,
            headerName: 'Name',
            filter: false,
            minWidth: 180,
            sortable: false,
            renderHeader: () => (
                <QuickSearchToolbar
                    theme
                    setPage={setPage}
                    setRows={setRows}
                    setSearchString={setSearchString}
                    instances={instanceData}
                />
            ),
            renderCell: renderName
        },
        {
            field: 'releaseVersion',
            headerName: 'Current Release',
            flex: 2,
            minWidth: 120,
            filter: false,
            sortable: false
        },
        {
            field: 'buildState',
            headerName: 'Upgrade To',
            flex: 5,
            minWidth: 270,
            filter: false,
            sortable: false,
            renderCell: renderUpgradeTo,
            headerAlign: 'center'
        },
        {
            field: 'updatedAt',
            headerName: 'Last updated date',
            minWidth: 150,
            valueFormatter: (params) =>
                params.value ? format(new Date(params.value), 'yyyy-M-dd HH:mm:ss') : 'NA',
            flex: 3,
            filter: false,
            sortable: false,
            type: 'datetime'
        },
        {
            field: 'updatedBy',
            headerName: 'Last updated by',
            filter: false,
            minWidth: 120,
            sortable: false,
            flex: 3,
            renderCell: renderUpdatedBy
        },
        {
            field: 'g2Review',
            headerName: 'G2 Review Widget',
            filter: false,
            minWidth: 120,
            sortable: false,
            flex: 3,
            renderCell: (params) => (
                <Switch
                    checked={params.row.g2Review || false}
                    onClick={() => openG2ReviewDialog(params)}
                />
            )
        },
        {
            field: 'action',
            headerName: 'Action',
            filter: false,
            minWidth: 170,
            sortable: false,
            flex: 4,
            renderCell: renderButtons
        }
    ];

    SimpleDialog.propTypes = {
        onClose: PropTypes.func.isRequired,
        open: PropTypes.bool.isRequired
    };

    function CustomPagination(props) {
        const apiRef = useGridApiContext();
        const [state] = useGridState(apiRef);
        const classes = useStyles();

        useEffect(() => {
            console.log('i m here', props.page);
            apiRef.current.setPage(props.page);
        }, [props.page, apiRef]);

        return (
            <div className={classes.customPagination}>
                <span className={classes.showPaginationDesc}>
                    Showing {state.pagination.page * 10 + 1} to&nbsp;
                    {(state.pagination.page + 1) * 10} of&nbsp;
                    {state.pagination.rowCount} entries
                </span>
                <Pagination
                    className={classes.root}
                    color="primary"
                    count={state.pagination.pageCount}
                    page={state.pagination.page + 1}
                    onChange={(event, value) => apiRef.current.setPage(value - 1)}
                    ariant="outlined"
                    shape="rounded"
                    hideNextButton={false}
                    variant="outlined"
                    renderItem={(item) => (
                        <PaginationItem {...item} className={classes.pageItem} variant="text" />
                    )}
                />
            </div>
        );
    }

    return (
        <div className={classes.container}>
            <Notifications />
            <Card variant="outlined" className={classes.containerCard}>
                <div className={classes.tableWrapper}>
                    <Typography variant="body1" className={classes.cardTitle}>
                        All Instances
                    </Typography>
                    <Button
                        variant="outlined"
                        component={Link}
                        to="/instances/create"
                        className={classes.newInstancesButton}
                    >
                        Add New Instance
                    </Button>
                    <div className={classes.tableContentWrapper}>
                        <DataGrid
                            rowHeight={40}
                            checkboxSelection={false}
                            className={classes.gridClass}
                            rows={rows}
                            columns={columns}
                            pageSize={10}
                            rowCount={rowCount}
                            onPageChange={(newPage) => setPage(newPage)}
                            paginationMode="server"
                            rowsPerPageOptions={[10]}
                            disableColumnSelector
                            disableColumnMenu
                            disableColumnFilter
                            autoHeight
                            renderingZone
                            disableSelectionOnClick
                            hideFooter={rowCount <= 10}
                            loading={loadingPagination}
                            componentsProps={{
                                pagination: { page }
                            }}
                            components={{
                                Pagination: CustomPagination,
                                NoRowsOverlay: () => (
                                    <Stack
                                        height="100%"
                                        alignItems="center"
                                        justifyContent="center"
                                        style={{ marginTop: '200px' }}
                                    >
                                        No Instances Found
                                    </Stack>
                                ),
                                NoResultsOverlay: () => (
                                    <Stack
                                        height="100%"
                                        alignItems="center"
                                        justifyContent="center"
                                        style={{ marginTop: '200px' }}
                                    >
                                        No Instances Found
                                    </Stack>
                                )
                            }}
                        />
                    </div>
                </div>
                <SimpleDialog open={open} selectedValue={selectedValue} onClose={handleClose} />
                <G2Review
                    openG2Review={openG2Review}
                    closeG2Review={closeG2Review}
                    toggleG2Review={toggleG2Review}
                    activeParams={activeParams}
                />
                <Services
                    open={openBuildDialog}
                    setOpenBuildDialog={setOpenBuildDialog}
                    clientSettings={currentClient}
                    startPreDeployment={startPreDeployment}
                    setCurrentClient={setCurrentClient}
                />
            </Card>
        </div>
    );
}
